/**
 * Env/credential application for a mid-session coding-agent key swap.
 *
 * The server's `cycleCodingAgentKey` RPC returns the replacement key's
 * credential env exactly as boot resolution shapes it (`decryptAllKeys`):
 * exactly one of CLAUDE_CODE_OAUTH_TOKEN / CONVEYOR_AGENT_KEY /
 * CONVEYOR_OPENCODE_OAUTH, plus CONVEYOR_TUI / CONVEYOR_AGENT_MODEL /
 * CONVEYOR_SUBSCRIPTION_KEY_LABEL. Applying it mirrors the existing
 * `onApiKeyUpdate` rotation handler (session-runner) — including clearing the
 * complementary auth vars so the old credential can't shadow the new one.
 */
import { ensureClaudeCredentials, removeConveyorCredentials } from "../harness/pty/credentials.js";

const FIVE_HOURS_MS = 5 * 60 * 60 * 1000;
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export function applyCycledKeyEnv(
  envVars: Record<string, string>,
  env: NodeJS.ProcessEnv = process.env,
): void {
  for (const [key, value] of Object.entries(envVars)) {
    env[key] = value;
  }
  if (envVars.CLAUDE_CODE_OAUTH_TOKEN) {
    // Subscription token auth: an env API key would win over it in the CLI.
    delete env.ANTHROPIC_API_KEY;
    if (!envVars.CONVEYOR_AGENT_KEY) delete env.CONVEYOR_AGENT_KEY;
  } else if (envVars.CONVEYOR_AGENT_KEY || envVars.CONVEYOR_OPENCODE_OAUTH) {
    delete env.CLAUDE_CODE_OAUTH_TOKEN;
  }
}

/** Bring the on-disk TUI credentials in line with the freshly applied env. */
export async function syncCredentialsAfterCycle(
  env: NodeJS.ProcessEnv = process.env,
): Promise<void> {
  if (env.CLAUDE_CODE_OAUTH_TOKEN) {
    await ensureClaudeCredentials(env);
  } else {
    await removeConveyorCredentials();
  }
}

/**
 * Pause target when neither the harness banner nor the server supplied a reset
 * time: hold for the full window (5h session / 24h weekly — matching the
 * server's own `resolveLimitedUntil` fallback).
 */
export function fallbackResetIso(rateLimitType: string, now = Date.now()): string {
  const weekly = /weekly|seven_day/i.test(rateLimitType);
  return new Date(now + (weekly ? TWENTY_FOUR_HOURS_MS : FIVE_HOURS_MS)).toISOString();
}
